/**
 * Lazy-load and autoplay muted inline videos on mobile (preload=none, data-src, etc.).
 */
(function () {
  const SELECTOR =
    'video.lazy-video, video[data-src], video[data-meet-src], video.tolstoy-hero-video, video.promotion-card-video';

  function shouldAutoplay(video) {
    return (
      video.hasAttribute('autoplay') ||
      video.classList.contains('lazy-video') ||
      video.classList.contains('meet-luncher-carousel-video') ||
      video.classList.contains('tolstoy-hero-video') ||
      video.classList.contains('promotion-card-video')
    );
  }

  function prepareVideo(video) {
    if (!shouldAutoplay(video)) return;
    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  }

  function getDeferredUrl(video) {
    return (
      video.getAttribute('data-meet-src') ||
      video.getAttribute('data-src') ||
      video.dataset.src ||
      ''
    );
  }

  function hydrateVideo(video) {
    if (!video) return false;

    const deferredUrl = getDeferredUrl(video);
    if (deferredUrl) {
      let source = video.querySelector('source');
      if (!source) {
        source = document.createElement('source');
        video.appendChild(source);
      }
      if (!source.getAttribute('src')) {
        source.src = deferredUrl;
        source.type = source.type || 'video/mp4';
      }
    }

    video.querySelectorAll('source[data-src]').forEach((source) => {
      if (!source.getAttribute('src')) {
        source.src = source.getAttribute('data-src');
      }
    });

    if (!video.querySelector('source[src]') && !video.getAttribute('src')) {
      return false;
    }

    if (video.dataset.mpVideoHydrated !== '1') {
      video.dataset.mpVideoHydrated = '1';
      if (video.getAttribute('data-meet-src')) {
        video.setAttribute('data-meet-hydrated', '1');
      }
      if (video.getAttribute('preload') === 'none') {
        video.setAttribute('preload', 'auto');
      }
      video.load();
    }

    return true;
  }

  function playWhenReady(video) {
    if (!video || !shouldAutoplay(video)) return;

    prepareVideo(video);

    const attempt = () => {
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {
          video.dataset.mpPlayPending = '1';
        });
      }
    };

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      attempt();
      return;
    }

    const onReady = () => {
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('loadeddata', onReady);
      attempt();
    };

    video.addEventListener('canplay', onReady, { once: true });
    video.addEventListener('loadeddata', onReady, { once: true });
  }

  function isVisibleEnough(video) {
    if (!video.isConnected) return false;
    let el = video;
    while (el) {
      const st = window.getComputedStyle(el);
      if (st.display === 'none' || st.visibility === 'hidden' || Number(st.opacity) === 0) {
        return false;
      }
      el = el.parentElement;
    }
    const rect = video.getBoundingClientRect();
    return rect.width > 1 && rect.height > 1 && rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function activateVideo(video) {
    if (!isVisibleEnough(video)) return;
    if (!hydrateVideo(video)) return;
    playWhenReady(video);
  }

  function isMeetLuncherCarouselVideo(video) {
    return video && video.classList.contains('meet-luncher-carousel-video');
  }

  function pauseVideo(video) {
    if (!video || video.paused || isMeetLuncherCarouselVideo(video)) return;
    try {
      video.pause();
    } catch (e) {
      /* ignore */
    }
  }

  function observeVideo(video) {
    if (video.dataset.mpVideoObserved === '1') return;
    video.dataset.mpVideoObserved = '1';

    if (!('IntersectionObserver' in window)) {
      activateVideo(video);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            activateVideo(video);
          } else if (shouldAutoplay(video)) {
            pauseVideo(video);
          }
        });
      },
      { rootMargin: '150px 0px', threshold: 0.12 }
    );

    io.observe(video);
    video._mpVideoIO = io;

    if (isVisibleEnough(video)) {
      activateVideo(video);
    }
  }

  function scan(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SELECTOR).forEach(observeVideo);
  }

  function retryPendingOnGesture() {
    document.querySelectorAll('video[data-mp-play-pending="1"]').forEach((video) => {
      delete video.dataset.mpPlayPending;
      if (!hydrateVideo(video)) return;
      playWhenReady(video);
    });
  }

  let gestureBound = false;
  function bindGestureRetry() {
    if (gestureBound) return;
    gestureBound = true;
    document.addEventListener('touchstart', retryPendingOnGesture, { passive: true });
    document.addEventListener('click', retryPendingOnGesture);
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState !== 'visible') return;
        scan();
        retryPendingOnGesture();
      },
      { passive: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      scan();
      bindGestureRetry();
    });
  } else {
    scan();
    bindGestureRetry();
  }

  window.addEventListener('load', () => scan(), { once: true });
  document.addEventListener('shopify:section:load', (event) => {
    scan(event.target || document);
  });

  window.mpLazyVideosScan = scan;
  window.mpActivateLazyVideo = activateVideo;
  window.mpPlayVideoWhenReady = playWhenReady;
})();
